import React from "react";
import type { Property } from "../PropiedadesPageNew"; // Ajusta la ruta

interface PropertyDetailProps {
    selectedProp: Property | null;
    isDrawerOpen: boolean;
    isDrawerLoading: boolean;
    onClose: () => void;
    onActionClick: (prop: Property, type: "status" | "delete" | "pending", e?: React.MouseEvent) => void;
}

const PropertyDetail: React.FC<PropertyDetailProps> = ({
    selectedProp,
    isDrawerOpen,
    isDrawerLoading,
    onClose,
    onActionClick
}) => {

    // Helper visual exclusivo de los detalles
    const getStatusColor = (status: string) => {
        switch (status) {
            case 'AC': return 'text-emerald-700 bg-emerald-50 border-emerald-200';
            case 'PE': return 'text-amber-700 bg-amber-50 border-amber-200';
            case 'SU': return 'text-red-700 bg-red-50 border-red-200';
            default: return 'text-gray-700 bg-gray-50 border-gray-200';
        }
    };

    return (
        <>
            {/* Overlay para cerrar haciendo clic afuera */}
            <div
                className={`fixed inset-0 bg-gray-900/30 backdrop-blur-[2px] z-40 transition-opacity duration-300 ${isDrawerOpen ? "opacity-100 visible" : "opacity-0 invisible"}`}
                onClick={onClose}
            ></div>

            {/* Panel lateral */}
            <div className={`fixed inset-y-0 right-0 z-50 w-full max-w-[340px] bg-white border-l border-gray-200 flex flex-col overflow-y-auto transform transition-transform duration-300 shadow-2xl ${isDrawerOpen ? "translate-x-0" : "translate-x-full"}`}>
                {selectedProp && (
                    <>
                        <div className="sticky top-0 bg-white/90 backdrop-blur-md z-10 p-[18px] px-5 border-b border-gray-100 flex justify-between items-center">
                            <h3 className="text-xs font-bold uppercase tracking-[1px] m-0 text-gray-500">Detalle de propiedad</h3>
                            <button
                                className="w-[30px] h-[30px] rounded-lg bg-gray-50 border border-gray-200 text-gray-500 flex items-center justify-center hover:text-gray-900 hover:bg-gray-100 transition-colors"
                                onClick={onClose}
                            >
                                ✕
                            </button>
                        </div>

                        {isDrawerLoading ? (
                            <div className="flex-1 flex flex-col items-center justify-center py-20">
                                <svg className="animate-spin h-8 w-8 text-[#4f6df5]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" strokeDasharray="30" strokeLinecap="round" /></svg>
                                <p className="mt-4 text-sm text-gray-500">Cargando detalle...</p>
                            </div>
                        ) : (
                            <>
                                <div className={`relative h-[160px] flex items-center justify-center bg-gradient-to-br ${selectedProp.gradient}`}>
                                    {selectedProp.mainPhotoUrl ? (
                                        <img
                                            src={selectedProp.mainPhotoUrl}
                                            alt={selectedProp.name}
                                            className="w-full h-full object-cover"
                                        />
                                    ) : (
                                        <span className="text-[60px] text-gray-500/50">{selectedProp.icon}</span>
                                    )}
                                </div>

                                <div className="p-4 px-5 border-b border-gray-100">
                                    <div className="text-[17px] font-bold mb-1 text-gray-900">{selectedProp.name}</div>
                                    <div className="text-[12px] text-gray-500 flex items-center gap-1 mb-2.5">
                                        <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></svg>
                                        {selectedProp.location}
                                    </div>
                                    <div className="flex gap-1.5 flex-wrap">
                                        <span className={`text-[10px] font-semibold px-2 py-[3px] rounded-md uppercase tracking-[0.5px] border ${getStatusColor(selectedProp.status)}`}>
                                            {selectedProp.statusLabel}
                                        </span>
                                        <span className="text-[10px] font-semibold px-2 py-[3px] rounded-md uppercase tracking-[0.5px] bg-indigo-50 text-[#4f6df5] border border-indigo-100">
                                            {selectedProp.type}
                                        </span>
                                        {selectedProp.rating && (
                                            <span className="text-[10px] font-semibold px-2 py-[3px] rounded-md uppercase tracking-[0.5px] bg-amber-50 text-amber-600 border border-amber-100">
                                                {selectedProp.rating} ★
                                            </span>
                                        )}
                                    </div>
                                </div>

                                <div className="p-3.5 px-5 border-b border-gray-100">
                                    <div className="text-[10px] font-bold uppercase tracking-[0.8px] text-gray-400 mb-2.5">Información general</div>
                                    <div className="flex justify-between py-1.5"><span className="text-[12px] text-gray-500">Anfitrión</span><span className="text-[12px] font-semibold text-[#4f6df5] cursor-pointer hover:underline">{selectedProp.host}</span></div>
                                    <div className="flex justify-between py-1.5">
                                        <span className="text-[12px] text-gray-500">Precio / noche</span>
                                        <span className="text-[12px] font-semibold text-emerald-600">{selectedProp.currency} {selectedProp.price}</span>
                                    </div>
                                    <div className="flex justify-between py-1.5"><span className="text-[12px] text-gray-500">Capacidad</span><span className="text-[12px] font-semibold text-gray-900">{selectedProp.capacity}</span></div>
                                    <div className="flex justify-between py-1.5"><span className="text-[12px] text-gray-500">Habitaciones</span><span className="text-[12px] font-semibold text-gray-900">{selectedProp.rooms}</span></div>
                                    <div className="flex justify-between py-1.5"><span className="text-[12px] text-gray-500">Superficie</span><span className="text-[12px] font-semibold text-gray-900">{selectedProp.area}</span></div>
                                </div>

                                <div className="p-3.5 px-5 border-b border-gray-100">
                                    <div className="text-[10px] font-bold uppercase tracking-[0.8px] text-gray-400 mb-2.5">Métricas</div>
                                    <div className="flex justify-between py-1.5"><span className="text-[12px] text-gray-500">Reservas totales</span><span className="text-[12px] font-semibold text-gray-900">{selectedProp.bookings}</span></div>
                                    <div className="flex justify-between py-1.5"><span className="text-[12px] text-gray-500">Tasa de ocupación</span><span className="text-[12px] font-semibold text-emerald-600">{selectedProp.occupancyRate}%</span></div>
                                    <div className="flex justify-between py-1.5"><span className="text-[12px] text-gray-500">Ingresos generados</span><span className="text-[12px] font-semibold text-emerald-600">${selectedProp.totalIncome?.toLocaleString()}</span></div>
                                    <div className="flex justify-between py-1.5"><span className="text-[12px] text-gray-500">Comisión JOM</span><span className="text-[12px] font-semibold text-gray-900">${selectedProp.jomCommission?.toLocaleString()}</span></div>
                                    <div className="flex justify-between py-1.5">
                                        <span className="text-[12px] text-gray-500">Rating</span>
                                        <span className="text-[12px] font-semibold text-amber-500">
                                            {selectedProp.rating ? `${selectedProp.rating} ★ (${selectedProp.reviews} ${selectedProp.reviews === 1 ? 'reseña' : 'reseñas'})` : '—'}
                                        </span>
                                    </div>
                                </div>

                                <div className="p-3.5 px-5 border-b border-gray-100">
                                    <div className="text-[10px] font-bold uppercase tracking-[0.8px] text-gray-400 mb-2.5">Amenidades</div>
                                    <div className="flex flex-wrap gap-1.5">
                                        {selectedProp.amenities?.map((amenity) => (
                                            <span key={amenity} className="text-[11px] px-2.5 py-1 rounded-md bg-gray-50 border border-gray-200 text-gray-600">
                                                {amenity}
                                            </span>
                                        ))}
                                    </div>
                                </div>

                                <div className="flex flex-col gap-2 p-4 px-5 mt-auto mb-4">
                                    {/* <button className="w-full p-2.5 rounded-lg text-xs font-semibold cursor-pointer transition-colors bg-[#4f6df5] text-white border-none hover:bg-[#3b5bdb] shadow-sm">
                                        Ver calendario
                                    </button> */}

                                    {/* <button className="w-full p-2.5 rounded-lg text-xs font-semibold cursor-pointer transition-colors bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 shadow-sm">
                                        Editar propiedad
                                    </button> */}

                                    {/* <button
                                        className="w-full p-2.5 rounded-lg text-xs font-semibold cursor-pointer transition-colors bg-white text-amber-600 border border-amber-200 hover:bg-amber-50 shadow-sm"
                                        onClick={(e) => onActionClick(selectedProp, "pending", e)}
                                    >
                                        Marcar pendiente
                                    </button> */}

                                    {selectedProp.status !== "SU" ? (
                                        <button
                                            className="w-full p-2.5 rounded-lg text-xs font-semibold cursor-pointer transition-colors bg-white text-red-600 border border-red-200 hover:bg-red-50 mt-2 shadow-sm"
                                            onClick={(e) => onActionClick(selectedProp, "status", e)}
                                        >
                                            Suspender propiedad
                                        </button>
                                    ) : (
                                        <button
                                            className="w-full p-2.5 rounded-lg text-xs font-semibold cursor-pointer transition-colors bg-white text-emerald-600 border border-emerald-200 hover:bg-emerald-50 mt-2 shadow-sm"
                                            onClick={(e) => onActionClick(selectedProp, "status", e)}
                                        >
                                            Reactivar propiedad
                                        </button>
                                    )}
                                </div>
                            </>
                        )}
                    </>
                )}
            </div>
        </>
    );
};

export default PropertyDetail;